import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const alertVariants = cva(
  "relative w-full rounded-lg border-2 px-4 py-3.5 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*5)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-1 items-start [&>svg]:size-5 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "bg-card text-foreground border-l-4 border-l-foreground/20",
        destructive:
          "text-destructive bg-destructive/5 border-destructive/30 border-l-4 border-l-destructive [&>svg]:text-current *:data-[slot=alert-description]:text-destructive/90",
        warning:
          "text-amber-700 bg-amber-50 border-amber-300 border-l-4 border-l-amber-500 dark:text-amber-400 dark:bg-amber-900/20 dark:border-amber-800 dark:border-l-amber-500 [&>svg]:text-current",
        success:
          "text-emerald-700 bg-emerald-50 border-emerald-300 border-l-4 border-l-emerald-500 dark:text-emerald-400 dark:bg-emerald-900/20 dark:border-emerald-800 dark:border-l-emerald-500 [&>svg]:text-current",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      data-variant={variant}
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "col-start-2 line-clamp-1 min-h-4 font-bold tracking-tight text-base",
        className
      )}
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "col-start-2 grid justify-items-start gap-1 text-sm font-medium [&_p]:leading-relaxed",
        className
      )}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription };
